import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { authAPI } from '../api/client';

const PermissionsContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { currentCompanyId, isAuthenticated, isSuperAdmin, loading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) {
      return; // Wait for auth to complete
    }
    
    if (isAuthenticated && currentCompanyId) {
      fetchPermissions();
    } else {
      setPermissions([]);
      setRoles([]);
      setLoading(false);
    }
  }, [currentCompanyId, isAuthenticated, authLoading]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getPermissions(currentCompanyId);
      setPermissions(response.data.permissions || []);
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Fetch permissions error:', error);
      setPermissions([]);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (permissionName) => {
    if (isSuperAdmin) return true;
    return permissions.includes(permissionName);
  };

  const hasAnyPermission = (permissionNames) => {
    if (isSuperAdmin) return true;
    return permissionNames.some(name => hasPermission(name));
  };

  const hasAllPermissions = (permissionNames) => {
    if (isSuperAdmin) return true;
    return permissionNames.every(name => hasPermission(name));
  };

  const hasRole = (roleName) => {
    if (isSuperAdmin) return true;
    return roles.some(r => r.RoleName === roleName);
  };

  const value = {
    permissions,
    roles,
    loading,
    isSuperAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    refreshPermissions: fetchPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
